import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { importAIBudget } from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';

import { useAuth } from '../../../../hooks/useAuth';
import { useGlobalPref } from '../../../../hooks/useGlobalPref';
import { useResponsive } from '../../../../ResponsiveProvider';
import { styles, theme } from '../../../../style';
import { warningText } from '../../../../style/themes/light';
import { Block } from '../../../common/Block';
import { Button } from '../../../common/Button';
import { Link } from '../../../common/Link';
import { Select } from '../../../common/Select';
import { Text } from '../../../common/Text';
import { View } from '../../../common/View';
import { Field, Row, SelectCell, Table } from '../../../table';

const ROW_HEIGHT = 43;

type AccountsData = Partial<{
  institutionName: string;
  isInstitution: boolean;
  account_id: string;
  connectionId: string;
  institution: {
    id: string;
    name: string;
  };
  mask: string;
  name: string;
  official_name: string;
}>;

type OnboardingAILinkAccountsProps = {
  accountsData: AccountsData[];
  onAddAccount: () => void;
};

const OnboardingAILinkAccounts: React.FC<OnboardingAILinkAccountsProps> = ({
  accountsData,
  onAddAccount,
}) => {
  const tableRef: React.MutableRefObject<any> = useRef();
  const { atLeastMediumWidth } = useResponsive();
  const [lastAccountsLength, setLastAccountsLength] = useState(0);

  const [fiscalYearStart] = useGlobalPref('fiscalYearStart');
  const { billingStatus } = useAuth();

  const [selectedAccounts, setSelectedAccounts] = useState<{
    [key: string]: string[];
  }>({});
  const [budgetTypeByAccountId, setBudgetTypeByAccountId] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (accountsData.length) {
      const _budgetTypeByAccountId = {
        ...budgetTypeByAccountId,
      };
      accountsData
        .filter(
          a =>
            !a.isInstitution &&
            budgetTypeByAccountId[a.account_id] === undefined,
        )
        .forEach(a => {
          _budgetTypeByAccountId[a.account_id] = true;
        });
      setBudgetTypeByAccountId(_budgetTypeByAccountId);
    }
  }, [accountsData.length]);

  useEffect(() => {
    if (accountsData.length !== lastAccountsLength && tableRef.current) {
      tableRef.current.scrollTo(accountsData.at(-1).account_id);
    }
    setLastAccountsLength(accountsData.length);
  }, [accountsData.length, tableRef.current]);

  const addedInstitutonsCount = useMemo(() => {
    return accountsData.filter(a => a.isInstitution).length;
  }, [accountsData.length]);

  const selectedAccountsChangeHandler = (connectionId, accountId) => {
    if (connectionId) {
      if (
        connectionId in selectedAccounts &&
        selectedAccounts[connectionId].includes(accountId)
      ) {
        const currentConnectionAccounts = selectedAccounts[connectionId].filter(
          s => s !== accountId,
        );
        const _selectedAccounts = structuredClone(selectedAccounts);
        _selectedAccounts[connectionId] = currentConnectionAccounts;
        setSelectedAccounts(_selectedAccounts);
      } else {
        const _selectedAccounts = structuredClone(selectedAccounts);
        _selectedAccounts[connectionId] = [
          ...(_selectedAccounts[connectionId] || []),
          accountId,
        ];
        setSelectedAccounts(_selectedAccounts);
      }
    }
  };

  const onLinkAccounts = async () => {
    setIsLoading(true);
    const connections = Object.keys(selectedAccounts).map(key => {
      return {
        connectionId: key,
        accounts: selectedAccounts[key].map(a => ({
          id: a,
          isOnBudget: budgetTypeByAccountId[a] ?? false,
        })),
      };
    });
    try {
      const aiOnboardingResponse = await send('ai-onboarding-quiltt', {
        connections,
      });
      const selectedAccountsByAccountId = new Map();
      for (const acc of Object.values(selectedAccounts).flatMap(sa => sa)) {
        const account = accountsData.find(a => a.account_id === acc);
        selectedAccountsByAccountId.set(account.account_id, account);
      }
      dispatch(
        importAIBudget(
          {
            payload: aiOnboardingResponse.data,
            fiscalYearStart,
            onboardingType: 'bank_sync',
            selectedAccounts: selectedAccountsByAccountId,
          },
          setIsLoading,
        ),
      );
    } catch (e) {
      setIsLoading(false);
      console.log('error', e);
    }
  };

  return (
    <View
      style={{ maxWidth: 540, minWidth: !atLeastMediumWidth ? '100%' : 540 }}
    >
      {isLoading ? (
        <View
          style={{
            flex: 1,
            gap: 20,
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 0',
            ...styles.delayedFadeIn,
          }}
        >
          <Block
            style={{
              marginBottom: 20,
              marginLeft: 25,
              marginRight: 25,
              fontSize: 16,
            }}
          >
            We are working to import and categorize your transactions, so we can
            create a budget file for you automatically.
            <br />
            <br />
            This might take a little while, treat yourself to a cup of coffee ☕
          </Block>
          <img
            style={{ verticalAlign: 'middle', marginLeft: 5 }}
            width={250}
            src="/images/coffee-sip.gif"
          />
        </View>
      ) : (
        <>
          <Text style={{ marginBottom: 10 }}>
            We found the following accounts. Select which ones you want to link:
          </Text>
          <View
            style={{
              flex: 'unset',
              height: 300,
              border: '1px solid ' + theme.tableBorder,
            }}
          >
            <Table
              ref={tableRef}
              rowHeight={ROW_HEIGHT}
              backgroundColor="transparent"
              style={{ flex: 1, backgroundColor: 'transparent' }}
              items={accountsData as (AccountsData & { id: number })[]}
              getItemKey={index => index}
              renderItem={({ item: account }) => {
                return account.isInstitution ? (
                  <Row
                    key={`${account.institutionName}`}
                    height={ROW_HEIGHT}
                    inset={0}
                    style={{
                      backgroundColor: theme.tableBackground,
                      color: theme.tableText,
                      ':hover': {
                        backgroundColor: theme.tableRowBackgroundHover,
                      },
                    }}
                  >
                    <Field width="flex" style={{ alignItems: 'flex-start' }}>
                      <Text style={{ fontWeight: 500 }}>
                        {account.institutionName}
                      </Text>
                    </Field>
                  </Row>
                ) : (
                  <Row
                    key={account.account_id}
                    height={ROW_HEIGHT}
                    inset={0}
                    style={{
                      backgroundColor: theme.tableBackground,
                      color: theme.tableText,
                      ':hover': {
                        backgroundColor: theme.tableRowBackgroundHover,
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      selectedAccountsChangeHandler(
                        account.connectionId,
                        account.account_id,
                      )
                    }
                  >
                    <Field width={38} name="select">
                      <SelectCell
                        exposed={true}
                        focused={false}
                        selected={selectedAccounts[
                          account.connectionId
                        ]?.includes(account.account_id)}
                        style={{
                          border: 'none',
                          height: '100%',
                          cursor: 'pointer',
                          paddingLeft: 8,
                        }}
                        onSelect={() =>
                          selectedAccountsChangeHandler(
                            account.connectionId,
                            account.account_id,
                          )
                        }
                      />
                    </Field>
                    <Field
                      width="flex"
                      name="name"
                      style={{ alignItems: 'flex-start' }}
                    >
                      <Text>{account.name}</Text>
                    </Field>
                    <Field
                      width={108}
                      name="on_off_budget"
                      style={{ alignItems: 'flex-start' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Select
                        options={[
                          ['1', 'For budget'],
                          ['0', 'Off budget'],
                        ]}
                        value={
                          budgetTypeByAccountId[account.account_id] ? '1' : '0'
                        }
                        onChange={value => {
                          const _budgetTypeByAccountId = {
                            ...budgetTypeByAccountId,
                          };
                          _budgetTypeByAccountId[account.account_id] =
                            value === '1';
                          setBudgetTypeByAccountId(_budgetTypeByAccountId);
                        }}
                      />
                    </Field>
                  </Row>
                );
              }}
            />
          </View>
          <View
            style={{
              flexDirection: atLeastMediumWidth ? 'row' : 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: 10,
              gap: 16,
            }}
          >
            {addedInstitutonsCount >= billingStatus.maxInstitutions ? (
              <span style={{ color: warningText }}>
                You have reached the maximum number of institutions added
                intended by free trial, if you need to add more
                <Link style={{ marginLeft: 3 }} to="/billing">
                  upgrade your billing plan
                </Link>
              </span>
            ) : null}
            <div
              style={{
                display: 'flex',
                gap: 8,
                minWidth: 'fit-content',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="normal"
                onClick={onAddAccount}
                disabled={
                  addedInstitutonsCount >= billingStatus.maxInstitutions
                }
              >
                Add another institution
              </Button>
              <Button
                type="primary"
                onClick={onLinkAccounts}
                disabled={
                  !Object.values(selectedAccounts).some(a => a.length) ||
                  isLoading
                }
              >
                All done
              </Button>
            </div>
          </View>
        </>
      )}
    </View>
  );
};
export default OnboardingAILinkAccounts;
